import React, { useState, useCallback, useMemo } from 'react';
import { Paper, Grid, Box, useMediaQuery, useTheme } from '@material-ui/core';
import { Tooltip } from '@material-ui/core';
import { InfoOutlined } from '@material-ui/icons';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { HtmlView, MTable, RepealableName } from '../../../../../Components';
import { OwnNotes } from '../../../../../Components/OwnNotes';
import { TextField as AppTextField, SelectField, DateField } from '../../../../../Components/Field';
import { StepData } from './types';
import { StepButtonBar } from './StepButtonBar';

interface Step8PreviewProps {
  planData: StepData;
  setStepData: (fn: (prev: StepData) => StepData) => void;
  selectedRows: any[];
  setSelectedRows: (rows: any[]) => void;
  selectedRowsData: any[];
  setSelectedRowsData: (data: any[]) => void;
  requirementType: any;
  setSelectedCompanyUnit: (u: any) => void;
  setSelectedRequirementType: (t: any) => void;
  companyUnits: any[];
  requirementTypeOptions: any[];
  reminderOptions: any[];
  onSubmit: (data?: StepData) => void;
  onBack: () => void;
  onCancel: () => void;
  isLoading: boolean;
  planType: 'one-time' | 'recurring' | 'from-completed';
}

const Step8Preview = ({
  planData, setStepData, selectedRows, setSelectedRows, selectedRowsData, setSelectedRowsData,
  requirementType, setSelectedCompanyUnit, setSelectedRequirementType,
  companyUnits, requirementTypeOptions, reminderOptions,
  onSubmit, onBack, onCancel, isLoading, planType,
}: Step8PreviewProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const paperPadding = isMobile ? theme.spacing(2) : theme.spacing(3);

  const [localData, setLocalData] = useState<StepData>({ ...planData });
  const localDataRef = React.useRef<StepData>(localData);
  localDataRef.current = localData;

  const updateLocal = useCallback((updates: Partial<StepData>) => {
    setLocalData(prev => ({ ...prev, ...updates }));
  }, []);

  const prevCompanyUnitRef = React.useRef(localData.CustomerCompanyUnitId);
  const prevTypeRef = React.useRef(localData.type);

  const handleNameChange = useCallback((val: any) => updateLocal({ name: val?.value || '' }), [updateLocal]);

  const handleCompanyUnitChange = useCallback((val: any) => {
    const newId = val?.value;
    updateLocal({ CustomerCompanyUnitId: newId, _companyUnitName: val?.label || '' });
    const unit = companyUnits.find((u: any) => u.id === newId);
    if (unit) setSelectedCompanyUnit(unit);
    if (newId !== prevCompanyUnitRef.current) {
      setSelectedRows([]);
      setSelectedRowsData([]);
      prevCompanyUnitRef.current = newId;
    }
  }, [updateLocal, companyUnits, setSelectedCompanyUnit, setSelectedRows, setSelectedRowsData]);

  const handleStartDateChange = useCallback((val: any) => {
    if (val?.value) {
      const d = val.value;
      updateLocal({ StartDate: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}` });
    }
  }, [updateLocal]);

  const handleEndDateChange = useCallback((val: any) => {
    if (val?.value) {
      const d = val.value;
      updateLocal({ EndDate: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}` });
    }
  }, [updateLocal]);

  const handleContentTypeChange = useCallback((val: any) => {
    const newType = val?.value;
    updateLocal({ type: newType, _typeName: val?.label || '' });
    const reqType = requirementTypeOptions.find((t: any) => t.id === newType);
    if (reqType) setSelectedRequirementType(reqType);
    if (newType !== prevTypeRef.current) {
      setSelectedRows([]);
      setSelectedRowsData([]);
      prevTypeRef.current = newType;
    }
  }, [updateLocal, requirementTypeOptions, setSelectedRequirementType, setSelectedRows, setSelectedRowsData]);

  const handleReminderChange = useCallback((val: any) => {
    updateLocal({ Reminder: val?.value, _reminderLabel: val?.label || '', _sendReminder: val != null });
  }, [updateLocal]);

  const handleRecurringDaysChange = useCallback((val: any) => {
    updateLocal({ RecurrentInterval: val?.value ? Number(val.value) : undefined });
  }, [updateLocal]);

  const handlePreviewBack = useCallback(() => {
    const latest = localDataRef.current;
    setStepData((prev: StepData) => ({ ...prev, ...latest }));
    if (latest.CustomerCompanyUnitId != null) {
      const unit = companyUnits.find((u: any) => u.id === latest.CustomerCompanyUnitId);
      if (unit) setSelectedCompanyUnit(unit);
    }
    if (latest.type != null) {
      const reqType = requirementTypeOptions.find((t: any) => t.id === latest.type);
      if (reqType) setSelectedRequirementType(reqType);
    }
    onBack();
  }, [setStepData, onBack, companyUnits, requirementTypeOptions, setSelectedCompanyUnit, setSelectedRequirementType]);

  const handlePreviewSubmit = useCallback(() => {
    const latest = localDataRef.current;
    setStepData((prev: StepData) => ({ ...prev, ...latest }));
    onSubmit(latest);
  }, [setStepData, onSubmit]);

  const companyUnitOptions = useMemo(() => companyUnits.map((u: any) => ({ value: u.id, label: u.name })), [companyUnits]);
  const typeOptions = useMemo(() => requirementTypeOptions.map((t: any) => ({ value: t.id, label: t.name })), [requirementTypeOptions]);
  const reminderSelectOptions = useMemo(() => reminderOptions.map((o: any) => ({ value: o.id, label: o.name })), [reminderOptions]);

  const isLaws = localData.type === 0;
  const isRecurring = planType === 'recurring' || planType === 'from-completed';

  const renderDate = (date?: Date) => date ? moment(date).format('ll') : undefined;

  const renderCommaSplit = (value: string | undefined) => {
    if (value) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {value.split(',').map((val: string, index: number) => <span key={index}>{val}</span>)}
        </div>
      );
    }
    return '-';
  };

  const columns: any[] = isLaws ? [
    { title: formatMessage({ id: 'law-portal.our-laws.table.number' }), field: 'number' },
    { title: formatMessage({ id: 'law-portal.our-laws.table.law' }), field: 'name', render: (row: any) => <RepealableName name={row.name} effectiveTo={row.effectiveTo} /> },
    { title: formatMessage({ id: 'law-portal.our-laws.table.description' }), field: 'description', render: (row: any) => <HtmlView variant="body2" value={row?.description} />, sorting: false },
    { title: formatMessage({ id: 'law-portal.our-laws.table.changeNumber' }), field: 'changeNumber' },
    { title: formatMessage({ id: 'law-portal.our-laws.table.changeEffectiveFrom' }), field: 'changeEffectiveFrom', render: (row: any) => renderDate(row.changeEffectiveFrom) },
    { title: formatMessage({ id: 'law-portal.our-laws.table.notes' }), field: 'notes', render: (row: any) => <OwnNotes {...row} />, sorting: false },
    { title: formatMessage({ id: 'law-portal.compliance.report.table.col' }), field: 'responsible', render: (row: any) => renderCommaSplit(row.responsible), sorting: false },
    { title: formatMessage({ id: 'utils.filters.processes' }), field: 'processes', render: (row: any) => renderCommaSplit(row.processes), sorting: false },
    { title: formatMessage({ id: 'utils.filters.aspects' }), field: 'aspects', render: (row: any) => renderCommaSplit(row.aspects), sorting: false },
    { title: formatMessage({ id: 'utils.filters.tags' }), field: 'tags', render: (row: any) => renderCommaSplit(row.tags), sorting: false },
    { title: formatMessage({ id: 'law-portal.our-laws.table.area' }), field: 'area' },
  ] : [
    { title: formatMessage({ id: 'law-portal.requirements.table.name' }), field: 'name', render: (row: any) => <RepealableName name={row.name} effectiveTo={row.effectiveTo} /> },
    { title: formatMessage({ id: 'law-portal.requirements.table.description' }), field: 'description', render: (row: any) => <HtmlView variant="body2" value={row.description} />, sorting: false },
    { title: formatMessage({ id: 'law-portal.requirements.table.area' }), field: 'area' },
    { title: formatMessage({ id: 'law-portal.requirements.table.changedDate' }), field: 'changedDate', render: (row: any) => renderDate(row.changedDate) },
    { title: formatMessage({ id: 'law-portal.requirements.table.notes' }), field: 'notes', render: (row: any) => <OwnNotes {...row} />, sorting: false },
    { title: formatMessage({ id: 'law-portal.compliance.report.table.col' }), field: 'responsible', render: (row: any) => renderCommaSplit(row.responsible), sorting: false },
    { title: formatMessage({ id: 'utils.filters.processes' }), field: 'processes', render: (row: any) => renderCommaSplit(row.processes), sorting: false },
    { title: formatMessage({ id: 'utils.filters.aspects' }), field: 'aspects', render: (row: any) => renderCommaSplit(row.aspects), sorting: false },
    { title: formatMessage({ id: 'utils.filters.tags' }), field: 'tags', render: (row: any) => renderCommaSplit(row.tags), sorting: false },
  ];

  return (
    <div>
      <Paper variant="outlined" style={{ padding: paperPadding, minHeight: '200px' }}>
        <Grid container spacing={isMobile ? 1 : 2}>
          <Grid item xs={12} sm={3}>
            <AppTextField
              name="preview-name"
              label={formatMessage({ id: 'law-portal.legal.planned.form.name' })}
              required
              placeholder="Enter plan name"
              errorTexts={[]}
              initialState={localData.name ? { value: localData.name } : undefined}
              onChange={handleNameChange}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <SelectField
              name="preview-company-unit"
              label={formatMessage({ id: 'law-portal.legal.planned.form.company.unit' })}
              required
              disable
              placeholder="Select..."
              options={companyUnitOptions}
              errorTexts={[]}
              initialState={localData.CustomerCompanyUnitId != null ? { value: localData.CustomerCompanyUnitId, label: localData._companyUnitName } : undefined}
              onChange={handleCompanyUnitChange}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DateField
              name="preview-start-date"
              label={formatMessage({ id: 'law-portal.legal.planned.form.start.date' })}
              required
              placeholder="YYYY-MM-DD"
              errorTexts={[]}
              initialState={localData.StartDate ? { value: new Date(localData.StartDate) } : undefined}
              onChange={handleStartDateChange}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DateField
              name="preview-end-date"
              label={formatMessage({ id: 'law-portal.legal.planned.form.end.date' })}
              required
              placeholder="YYYY-MM-DD"
              errorTexts={[]}
              initialState={localData.EndDate ? { value: new Date(localData.EndDate) } : undefined}
              onChange={handleEndDateChange}
            />
          </Grid>
        </Grid>

        <Grid container spacing={isMobile ? 1 : 2} style={{ marginTop: '8px', alignItems: 'center' }}>
          <Grid item xs={12} sm={4}>
            <SelectField
              name="preview-content-type"
              label={formatMessage({ id: 'law-portal.create-plan.form-field-type' })}
              required
              disable
              placeholder="Select..."
              options={typeOptions}
              errorTexts={[]}
              initialState={localData.type != null ? { value: localData.type, label: requirementType?.name || localData._typeName } : undefined}
              onChange={handleContentTypeChange}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SelectField
              name="preview-reminder"
              label={formatMessage({ id: 'law-portal.compliance.create.plan.field.utskick' })}
              placeholder="Select..."
              options={reminderSelectOptions}
              errorTexts={[]}
              initialState={localData.Reminder != null ? { value: localData.Reminder, label: localData._reminderLabel } : undefined}
              onChange={handleReminderChange}
              icon={
                <Tooltip title={formatMessage({ id: 'law-portal.compliance.create.plan.field.utskick.tooltip.msg' })} placement="right-start">
                  <InfoOutlined style={{ fontSize: '20px' }} />
                </Tooltip>
              }
            />
          </Grid>
          {isRecurring && (
            <Grid item xs={12} sm={4}>
              <AppTextField
                name="preview-recurring-days"
                label={formatMessage({ id: 'law-portal.compliance.create.plan.field.intervall' })}
                placeholder={formatMessage({ id: 'law-portal.legal.number.of.day' })}
                type="number"
                errorTexts={[]}
                initialState={localData.RecurrentInterval != null ? { value: localData.RecurrentInterval.toString() } : undefined}
                onChange={handleRecurringDaysChange}
                icon={
                  <Tooltip title={formatMessage({ id: 'law-portal.compliance.create.plan.field.intervall.tooltip.msg' })} placement="right-start">
                    <InfoOutlined style={{ fontSize: '20px' }} />
                  </Tooltip>
                }
              />
            </Grid>
          )}
        </Grid>

        <Box style={{ marginTop: theme.spacing(2), maxHeight: '400px', overflowX: 'auto', overflowY: 'auto', width: '100%' }}>
          <MTable
            columns={columns}
            data={(selectedRowsData || []).map(({ tableData, ...rest }: any) => ({ ...rest }))}
            options={{ search: false, paging: false, toolbar: false, sorting: false, draggable: false, columnsButton: false }}
            components={{}}
          />
        </Box>
      </Paper>
      <StepButtonBar
        onCancel={onCancel}
        onBack={handlePreviewBack}
        onNext={handlePreviewSubmit}
        nextLabel="lawpoertal.evalution.plan.step.save-and-create"
        isLoading={isLoading}
        formatMessage={formatMessage}
      />
    </div>
  );
};

export default Step8Preview;